import * as THREE from 'three';

/*
 * Shell-texture fur shared by the Kli models.
 * glTF can't carry particle hair, so the body mesh is drawn `shells` times, each copy
 * pushed a little further out along its normals; each shell keeps only the pixels that
 * sit on a strand. Fur length per vertex comes from the mesh's COLOR_0.r, which the
 * Blender scripts export from the same density map that drives the particle hair
 * (bald around eyes and earbuds, none under clothes, thinner under the belly).
 */

// KLIENTO blues: the logo colour --k-500 #2DACDF at the root, --k-300 #7DCBEB at the tip
export const KLIENTO_FUR = { root: '#2DACDF', tip: '#7DCBEB' };

export function createFurMaterial({ shells, length = 0.2, density = 85, wind = true, colors = KLIENTO_FUR }) {
  return new THREE.ShaderMaterial({
    toneMapped: false,     // keep the brand blues exact
    vertexColors: true,    // exposes the COLOR_0 density map as `color`
    uniforms: {
      uShells: { value: shells },
      uLength: { value: length },
      uDensity: { value: density },
      uRoot: { value: new THREE.Color(colors.root) },
      uTip: { value: new THREE.Color(colors.tip) },
      uTime: { value: 0 },
      uWind: { value: wind ? 1 : 0 },
      uOmega: { value: new THREE.Vector3() },   // angular velocity, object space
      uVel: { value: new THREE.Vector3() },     // linear velocity, object space
      uLightDir: { value: new THREE.Vector3(0.5, 0.6, 0.6).normalize() },
    },
    vertexShader: /* glsl */`
      uniform float uShells, uLength, uTime, uWind;
      uniform vec3 uOmega, uVel;
      varying vec3 vBase, vN, vNormalV, vViewPos;
      varying float vH, vLen;

      void main() {
        float h = float(gl_InstanceID) / (uShells - 1.0);
        vec3 n = normalize(normal);
        float len = uLength * color.r;            // density map from Blender

        vec3 p = position + n * len * h;
        float bend = h * h;                                        // tips move, roots don't
        vec3 lag = -cross(uOmega, position) * 0.06 - uVel * 0.08;  // fur trails behind motion
        vec3 droop = vec3(0.0, -0.7 * len, 0.0);
        // Low-frequency flow in the surface plane: soft waves instead of spikes
        vec3 flow = vec3(sin(position.y * 5.0 + position.z * 3.0),
                         cos(position.x * 4.0 + position.z * 5.0),
                         sin(position.x * 3.0 - position.y * 4.0));
        flow -= n * dot(flow, n);
        vec3 wind = vec3(sin(uTime * 1.3 + position.y * 4.0), 0.0, cos(uTime * 1.1 + position.x * 4.0)) * 0.012 * uWind;
        p += (droop + flow * 0.45 * len + lag + wind) * bend;

        vBase = position;
        vN = n;
        vH = h;
        vLen = len;
        vNormalV = normalize(normalMatrix * n);
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        vViewPos = mv.xyz;
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      uniform float uDensity;
      uniform vec3 uRoot, uTip, uLightDir;
      varying vec3 vBase, vN, vNormalV, vViewPos;
      varying float vH, vLen;

      float hash(vec3 p) {
        p = fract(p * 0.3183099 + 0.1);
        p *= 17.0;
        return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
      }

      void main() {
        // One strand per 3D cell, at a random spot inside it (a regular lattice shows up as
        // rings on a sphere). Thickness is measured across the strand, ignoring depth.
        vec3 q = vBase * uDensity;
        vec3 cell = floor(q);
        vec3 jitter = vec3(hash(cell), hash(cell + 17.1), hash(cell + 31.7)) - 0.5;
        vec3 f = fract(q) - 0.5 - jitter * 0.6;
        vec3 n = normalize(vN);
        f -= n * dot(f, n);
        float rnd = hash(cell + 5.3);
        if (vH > 0.0) {
          if (vLen < 0.004) discard;
          float t = vH / mix(0.5, 1.0, rnd);
          if (t > 1.0 || length(f) > 0.5 * (1.0 - t)) discard;
        }

        vec3 col = mix(uRoot, uTip, smoothstep(0.0, 0.9, vH)) * mix(0.85, 1.1, rnd);
        vec3 nrm = normalize(vNormalV);
        float diff = max(dot(nrm, uLightDir) * 0.6 + 0.4, 0.0);   // wrapped light: fur scatters
        float ao = mix(0.6, 1.0, vH);                               // deeper fur is darker
        float rim = pow(1.0 - max(dot(nrm, normalize(-vViewPos)), 0.0), 3.0);
        vec3 lit = col * (0.5 + 0.6 * diff) * ao + uTip * rim * 0.5 * vH;

        gl_FragColor = vec4(lit, 1.0);
        #include <colorspace_fragment>
      }
    `,
  });
}

/** Replace a body mesh's surface with fur shells. Returns the InstancedMesh. */
export function addFur(body, material, shells) {
  body.material = new THREE.MeshBasicMaterial({ visible: false });   // the innermost shell is the skin
  const fur = new THREE.InstancedMesh(body.geometry, material, shells);
  fur.frustumCulled = false;
  fur.raycast = () => {};   // clicks hit the body, not 40 shells
  body.add(fur);
  return fur;
}

const tmp = new THREE.Vector3();
/** Feed motion into the fur: rotation/position deltas since the last frame. */
export function updateFurMotion(material, { dt, t, rotX, rotY, prevRotX, prevRotY, y, prevY }) {
  const u = material.uniforms;
  u.uTime.value = t;
  if (dt <= 0) return;   // dt can be 0 on the first frame; dividing by it would put NaN into the fur
  u.uOmega.value.lerp(tmp.set((rotX - prevRotX) / dt, (rotY - prevRotY) / dt, 0), 0.3);
  u.uVel.value.lerp(tmp.set(0, (y - prevY) / dt, 0), 0.3);
}

/** Light direction (world) -> view space, for the fur's wrapped diffuse. */
export function setFurLight(material, lightWorldPos, camera) {
  material.uniforms.uLightDir.value.copy(lightWorldPos).normalize().transformDirection(camera.matrixWorldInverse);
}
